import { useEffect, useState } from 'react'
import { FieldErrors, UseFormRegister } from 'react-hook-form'
import { ChevronIcon, CloseIcon } from 'components/icons'
import ProductImage from 'components/masterData/productImage'
import { TextInput } from 'components/common'
import { CatalogueProductOption } from 'types/bundleProduct'
import { getProductDisplayName, getProductPackInfo } from './utils'

type BundleItemFields = {
  catalogue: string
  price?: number | null
  specialPrice?: number | null
  quantity?: number | null
}

type BundleItemsFormShape = {
  bundleItems: BundleItemFields[]
}

type Props = {
  index: number
  product?: CatalogueProductOption
  register: UseFormRegister<any>
  errors: FieldErrors<BundleItemsFormShape>
  onRemove: () => void
  defaultExpanded?: boolean
}

const BundleItemCard = ({
  index,
  product,
  register,
  errors,
  onRemove,
  defaultExpanded = false,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const packInfo = getProductPackInfo(product)
  const itemErrors = errors?.bundleItems?.[index]
  const restInfo = [product?.itemNo, packInfo].filter(Boolean).join(' · ')
  const hasPricingError = Boolean(
    itemErrors?.price || itemErrors?.specialPrice || itemErrors?.quantity,
  )

  useEffect(() => {
    if (hasPricingError) setIsExpanded(true)
    // eslint-disable-next-line
  }, [hasPricingError])

  return (
    <div className="relative">
      {/* Component index tag */}
      <div className="absolute left-0 top-0 flex w-9 justify-center sm:w-11">
        <span className="mt-4 flex h-6 min-w-6 items-center justify-center rounded-full bg-amber-50 px-1.5 font-mono text-[11px] font-semibold tabular-nums text-amber-700 ring-1 ring-inset ring-amber-200">
          {String(index + 1).padStart(2, '0')}
        </span>
      </div>

      <div className="pl-9 sm:pl-11">
        <div className="flex items-start gap-3 py-3.5 pr-4">
          <ProductImage
            src={product?.image ?? null}
            alt={getProductDisplayName(product)}
            fallbackText=""
            size={56}
            className="shrink-0 border-gray-100"
          />

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-primary">
              {getProductDisplayName(product)}
            </p>
            <p className="mt-0.5 truncate font-mono text-[11px] tracking-[0.15em] text-gray-400">
              {product?._id || '—'}
            </p>
            {restInfo && (
              <p className="mt-0.5 truncate text-xs text-gray-400">
                {restInfo}
              </p>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => setIsExpanded((prev) => !prev)}
              aria-label={isExpanded ? 'Collapse pricing' : 'Edit pricing'}
              aria-expanded={isExpanded}
              className={`flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors ${
                isExpanded
                  ? 'border-primary bg-primary text-white'
                  : hasPricingError
                    ? 'border-error/40 bg-error/5 text-error hover:bg-error/10'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-primary'
              }`}
            >
              Pricing
              <span
                className={`h-2.5 w-2.5 shrink-0 transition-transform duration-300 ${
                  isExpanded ? 'rotate-180' : ''
                }`}
              >
                <ChevronIcon />
              </span>
            </button>

            <button
              type="button"
              onClick={onRemove}
              aria-label="Remove component"
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-300 transition-colors hover:bg-error/5 hover:text-error"
            >
              <span className="h-3 w-3">
                <CloseIcon />
              </span>
            </button>
          </div>
        </div>

        <div
          className={`grid overflow-hidden transition-all duration-300 ease-out ${
            isExpanded
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0'
          }`}
        >
          <div className="min-h-0">
            <div className="mb-4 mr-4 rounded-2xl border border-gray-100 bg-gray-50/60 p-4">
              <div className="grid gap-3.5 sm:grid-cols-3">
                <TextInput
                  label="Price (per unit)"
                  name={`bundleItems.${index}.price`}
                  type="number"
                  step="any"
                  required
                  autoComplete="off"
                  register={register}
                  error={itemErrors?.price?.message as string}
                />
                <TextInput
                  label="Special price (per unit)"
                  name={`bundleItems.${index}.specialPrice`}
                  type="number"
                  step="any"
                  autoComplete="off"
                  register={register}
                  error={itemErrors?.specialPrice?.message as string}
                />
                <TextInput
                  label="Quantity"
                  name={`bundleItems.${index}.quantity`}
                  type="number"
                  min={1}
                  step={1}
                  autoComplete="off"
                  register={register}
                  error={itemErrors?.quantity?.message as string}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BundleItemCard
